import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL2 } from "./service/apiService";

const MealsHome = () => {
  const [meals, setMeals] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddSiteForm, setShowAddSiteForm] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [filterSiteId, setFilterSiteId] = useState("");

  const [formData, setFormData] = useState({
    site_id: "",
    meal_date: "",
    meal_type: "L",
    quantity: "",
    remarks: "",
  });

  const [newSite, setNewSite] = useState({ site_name: "", kitchen_group: "" });

  const kitchenId = localStorage.getItem("kitchen_id") || "";

  const fetchSites = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL2}/meals_sites.php`);
      if (res.data.status) {
        setSites(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching sites:", err);
    }
  }, []);

  const fetchMeals = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL2}/meals_data.php?`;
      if (filterStartDate && filterEndDate)
        url += `start_date=${filterStartDate}&end_date=${filterEndDate}&`;
      if (filterSiteId) url += `site_id=${filterSiteId}&`;
      const res = await axios.get(url);
      if (res.data.status) {
        setMeals(res.data.data);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error("Error fetching meals:", err);
    }
    setLoading(false);
  }, [filterStartDate, filterEndDate, filterSiteId]);

  useEffect(() => {
    fetchSites();
    fetchMeals();
  }, [fetchSites, fetchMeals]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadResult(null);

    const fd = new FormData();
    fd.append("file", file);
    fd.append("kitchen_id", kitchenId);

    try {
      const res = await axios.post(`${API_BASE_URL2}/meals_upload.php`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(res.data);
      if (res.data.status) {
        fetchMeals();
        fetchSites();
      }
    } catch (err) {
      setUploadResult({ status: false, message: "Upload failed: " + err.message });
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleAddMeal = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_BASE_URL2}/meals_data.php`, {
        action: "add",
        ...formData,
        kitchen_id: kitchenId,
        quantity: parseInt(formData.quantity) || 0,
      });
      if (res.data.status) {
        setFormData({ site_id: "", meal_date: "", meal_type: "L", quantity: "", remarks: "" });
        setShowAddForm(false);
        fetchMeals();
      } else {
        alert(res.data.message);
      }
    } catch (err) {
      alert("Error adding meal: " + err.message);
    }
  };

  const handleAddSite = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_BASE_URL2}/meals_sites.php`, newSite);
      if (res.data.status) {
        setNewSite({ site_name: "", kitchen_group: "" });
        setShowAddSiteForm(false);
        fetchSites();
      } else {
        alert(res.data.message);
      }
    } catch (err) {
      alert("Error adding site: " + err.message);
    }
  };

  const handleDeleteMeal = async (mealId) => {
    if (!window.confirm("Are you sure you want to delete this meal entry?")) return;
    try {
      await axios.post(`${API_BASE_URL2}/meals_data.php`, {
        action: "delete",
        meal_id: mealId,
      });
      fetchMeals();
    } catch (err) {
      alert("Error deleting: " + err.message);
    }
  };

  const mealTypeLabel = (t) => {
    if (t === "B") return "Breakfast";
    if (t === "L") return "Lunch";
    if (t === "D") return "Dinner";
    return t;
  };

  const mealTypeBadge = (t) => {
    if (t === "B") return "bg-info";
    if (t === "L") return "bg-success";
    if (t === "D") return "bg-warning text-dark";
    return "bg-secondary";
  };

  return (
    <div className="content">
      <div className="container-fluid">
        <div className="row g-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center bg-white">
                <h5 className="mb-0">
                  <i className="bi bi-egg-fried me-2"></i>Meals Management
                </h5>
                <Link to="/MealsReport" className="btn btn-outline-primary btn-sm">
                  <i className="bi bi-bar-chart me-1"></i>View Report
                </Link>
              </div>
              <div className="card-body">
                {/* Action Buttons */}
                <div className="row mb-3">
                  <div className="col-md-4 mb-2">
                    <label className="btn btn-primary w-100" style={{ cursor: "pointer" }}>
                      <i className="bi bi-upload me-1"></i>
                      {uploading ? "Uploading..." : "Upload Excel File"}
                      <input
                        type="file"
                        accept=".xls,.xlsx"
                        onChange={handleUpload}
                        style={{ display: "none" }}
                        disabled={uploading}
                      />
                    </label>
                  </div>
                  <div className="col-md-4 mb-2">
                    <button
                      className="btn btn-success w-100"
                      onClick={() => { setShowAddForm(!showAddForm); setShowAddSiteForm(false); }}
                    >
                      <i className="bi bi-plus-circle me-1"></i>Add Meal Manually
                    </button>
                  </div>
                  <div className="col-md-4 mb-2">
                    <button
                      className="btn btn-outline-secondary w-100"
                      onClick={() => { setShowAddSiteForm(!showAddSiteForm); setShowAddForm(false); }}
                    >
                      <i className="bi bi-geo-alt me-1"></i>Add New Site
                    </button>
                  </div>
                </div>

                {/* Upload Result */}
                {uploadResult && (
                  <div className={`alert ${uploadResult.status ? "alert-success" : "alert-danger"} alert-dismissible fade show`}>
                    {uploadResult.status ? (
                      <>
                        <strong>Upload Successful!</strong> {uploadResult.inserted} records inserted/updated,{" "}
                        {uploadResult.skipped} skipped, {uploadResult.sites_found} sites found.
                      </>
                    ) : (
                      <><strong>Error:</strong> {uploadResult.message}</>
                    )}
                    <button type="button" className="btn-close" onClick={() => setUploadResult(null)}></button>
                  </div>
                )}

                {/* Add Meal Form */}
                {showAddForm && (
                  <div className="card mb-3 border-success">
                    <div className="card-body">
                      <h6 className="card-title">Add Meal Entry</h6>
                      <form onSubmit={handleAddMeal}>
                        <div className="row g-2">
                          <div className="col-md-3">
                            <label className="form-label">Site</label>
                            <select
                              className="form-select"
                              value={formData.site_id}
                              onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}
                              required
                            >
                              <option value="">Select Site</option>
                              {sites.map((s) => (
                                <option key={s.site_id} value={s.site_id}>
                                  {s.site_name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-2">
                            <label className="form-label">Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={formData.meal_date}
                              onChange={(e) => setFormData({ ...formData, meal_date: e.target.value })}
                              required
                            />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label">Meal Type</label>
                            <select
                              className="form-select"
                              value={formData.meal_type}
                              onChange={(e) => setFormData({ ...formData, meal_type: e.target.value })}
                            >
                              <option value="B">Breakfast</option>
                              <option value="L">Lunch</option>
                              <option value="D">Dinner</option>
                            </select>
                          </div>
                          <div className="col-md-2">
                            <label className="form-label">Quantity</label>
                            <input
                              type="number"
                              className="form-control"
                              value={formData.quantity}
                              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                              required
                              min="0"
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Remarks</label>
                            <input
                              type="text"
                              className="form-control"
                              value={formData.remarks}
                              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="mt-2">
                          <button type="submit" className="btn btn-success btn-sm me-2">
                            <i className="bi bi-check-lg me-1"></i>Save
                          </button>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowAddForm(false)}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Add Site Form */}
                {showAddSiteForm && (
                  <div className="card mb-3 border-secondary">
                    <div className="card-body">
                      <h6 className="card-title">Add New Site</h6>
                      <form onSubmit={handleAddSite}>
                        <div className="row g-2">
                          <div className="col-md-5">
                            <label className="form-label">Site Name</label>
                            <input
                              type="text"
                              className="form-control"
                              value={newSite.site_name}
                              onChange={(e) => setNewSite({ ...newSite, site_name: e.target.value })}
                              required
                              placeholder="e.g. Golf Hills I (Sec-79)"
                            />
                          </div>
                          <div className="col-md-5">
                            <label className="form-label">Kitchen Group</label>
                            <input
                              type="text"
                              className="form-control"
                              value={newSite.kitchen_group}
                              onChange={(e) => setNewSite({ ...newSite, kitchen_group: e.target.value })}
                              placeholder="e.g. Meals from 79 Kitchen"
                            />
                          </div>
                          <div className="col-md-2 d-flex align-items-end">
                            <button type="submit" className="btn btn-primary btn-sm me-2">Save</button>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowAddSiteForm(false)}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Filters */}
                <div className="row g-2 mb-3">
                  <div className="col-md-3">
                    <label className="form-label small">From Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={filterStartDate}
                      onChange={(e) => setFilterStartDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small">To Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={filterEndDate}
                      onChange={(e) => setFilterEndDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small">Site</label>
                    <select
                      className="form-select form-select-sm"
                      value={filterSiteId}
                      onChange={(e) => setFilterSiteId(e.target.value)}
                    >
                      <option value="">All Sites</option>
                      {sites.map((s) => (
                        <option key={s.site_id} value={s.site_id}>
                          {s.site_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3 d-flex align-items-end gap-2">
                    <button className="btn btn-primary btn-sm" onClick={fetchMeals}>
                      <i className="bi bi-search me-1"></i>Search
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => {
                        setFilterStartDate("");
                        setFilterEndDate("");
                        setFilterSiteId("");
                      }}
                    >
                      Reset
                    </button>
                  </div>
                </div>

                {/* Data Table */}
                <div className="table-responsive">
                  <table className="table table-hover table-bordered table-sm">
                    <thead className="table-dark">
                      <tr>
                        <th>#</th>
                        <th>Date</th>
                        <th>Site</th>
                        <th>Kitchen Group</th>
                        <th>Meal Type</th>
                        <th>Quantity</th>
                        <th>Remarks</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="8" className="text-center py-3">
                            <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                            Loading...
                          </td>
                        </tr>
                      ) : meals.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center text-muted py-3">
                            No meal data found. Upload an Excel file or add manually.
                          </td>
                        </tr>
                      ) : (
                        meals.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage).map((m, idx) => (
                          <tr key={m.meal_id}>
                            <td>{(currentPage - 1) * recordsPerPage + idx + 1}</td>
                            <td>{m.meal_date}</td>
                            <td>{m.site_name}</td>
                            <td><small className="text-muted">{m.kitchen_group}</small></td>
                            <td>
                              <span className={`badge ${mealTypeBadge(m.meal_type)}`}>
                                {mealTypeLabel(m.meal_type)}
                              </span>
                            </td>
                            <td className="fw-bold">{m.quantity}</td>
                            <td><small>{m.remarks}</small></td>
                            <td>
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleDeleteMeal(m.meal_id)}
                                title="Delete"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {!loading && meals.length > 0 && (() => {
                  const totalPages = Math.ceil(meals.length / recordsPerPage);
                  const startRecord = (currentPage - 1) * recordsPerPage + 1;
                  const endRecord = Math.min(currentPage * recordsPerPage, meals.length);
                  const maxButtons = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
                  let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                  if (endPage - startPage + 1 < maxButtons) startPage = Math.max(1, endPage - maxButtons + 1);
                  const pageNumbers = [];
                  for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

                  return (
                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <div className="text-muted small">
                        Showing {startRecord}-{endRecord} of {meals.length} records
                      </div>
                      {totalPages > 1 && (
                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                              <button className="page-link" onClick={() => setCurrentPage(1)}>&laquo;</button>
                            </li>
                            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                              <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>&lsaquo;</button>
                            </li>
                            {pageNumbers.map((p) => (
                              <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                                <button className="page-link" onClick={() => setCurrentPage(p)}>{p}</button>
                              </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                              <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>&rsaquo;</button>
                            </li>
                            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                              <button className="page-link" onClick={() => setCurrentPage(totalPages)}>&raquo;</button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MealsHome;
